import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPencil } from "@fortawesome/free-solid-svg-icons";
import { showToast } from "../../components/toast";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import useDoubleScroll from "../../helpers/useDoubleScroll";
import "../../Subscriber/subscribe.css";

const SalaryReport = () => {
  const [salaryReport, setSalaryReport] = useState([]);
  const [nameSearch, setNameSearch] = useState("");
  const [monthList, setMonthList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [inReport, setInReport] = useState([]);
  const [otherAllowance, setOtherAllowance] = useState(0);
  const [otherDeduction, setOtherDeduction] = useState(0);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bankExporting, setBankExporting] = useState(false);
  const [attendanceExporting, setAttendanceExporting] = useState(false);
  const [downloadingPayslip, setDownloadingPayslip] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState({}); // Add this new state

  const filteredSalaryReport = salaryReport.filter((row) => {
    if (!nameSearch.trim()) return true;
    const name = row.user_fullname || "";
    return name.toLowerCase().includes(nameSearch.toLowerCase().trim());
  });

  const tableContainerRef = useRef(null);
  const topScrollRef = useDoubleScroll(tableContainerRef, [salaryReport, filteredSalaryReport]);

  const token = localStorage.getItem("authToken");
  const toastShownRef = useRef(false);

  useEffect(() => {
    fetchMonthList();
  }, []);

  const handleDownloadPayslip = async (employeeId) => {
    if (!employeeId) {
      showToast.error("Employee ID is required for download");
      return;
    }

    try {
      setDownloadingIds(prev => ({ ...prev, [employeeId]: true })); // Set loading for specific ID
      const response = await axios.get(
        `${API_URL}payroll/salary-report/download-payslip/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
          params: {
            mid: selectedMonth // Pass the selected month as parameter if needed
          }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Payslip_${employeeId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Payslip downloaded successfully!");
    } catch (error) {
      console.error("Payslip download error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to download payslip";
      showToast.error(errorMessage);
    } finally {
      setDownloadingIds(prev => ({ ...prev, [employeeId]: false })); // Clear loading for specific ID
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedMonth) {
      showToast.error("Please select a month before generating report");
      return;
    }

    try {
      setGenerating(true);
      setError("");

      const response = await axios.post(
        `${API_URL}payroll/salary-report/generate?mid=${selectedMonth}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === 201 || response.data.success) {
        showToast.success(
          response.data.message || "Report generated successfully!"
        );
        fetchReport();
      } else {
        showToast.success(response.data.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Report generation error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to generate report";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleExportBank = async () => {
    if (!selectedMonth) {
      showToast.error("Please select a month before exporting");
      return;
    }

    try {
      setBankExporting(true);
      setError("");

      const response = await axios.get(
        `${API_URL}payroll/salary-report/bank-salary-export?format=xlsx&mid=${selectedMonth}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const selectedMonthObj = monthList.find((m) => String(m.value) === String(selectedMonth));
      const monthName = selectedMonthObj ? selectedMonthObj.name.toLowerCase().replace(/\s*-\s*/g, "_").replace(/\s+/g, "_") : selectedMonth;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bank_Report_${monthName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Bank report exported successfully!");
    } catch (error) {
      console.error("Bank export error", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export bank report";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setBankExporting(false);
    }
  };

  const handleExportAttendance = async () => {
    if (!selectedMonth) {
      showToast.error("Please select a month before exporting");
      return;
    }

    try {
      setAttendanceExporting(true);
      setError("");

      const response = await axios.get(
        `${API_URL}payroll/salary-report/attendance-export?mid=${selectedMonth}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const selectedMonthObj = monthList.find((m) => String(m.value) === String(selectedMonth));
      const monthName = selectedMonthObj ? selectedMonthObj.name.toLowerCase().replace(/\s*-\s*/g, "_").replace(/\s+/g, "_") : selectedMonth;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Attendance_Report_${monthName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Attendance report exported successfully!");
    } catch (error) {
      console.error("Attendance export error", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export attendance report";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setAttendanceExporting(false);
    }
  };

  const handleExport = async () => {
    if (!selectedMonth) {
      showToast.error("Please select a month before exporting");
      return;
    }

    try {
      setExporting(true);
      setError("");

      const response = await axios.post(
        `${API_URL}payroll/salary-report/export-report`,
        { smonth: selectedMonth },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      const selectedMonthObj = monthList.find((m) => String(m.value) === String(selectedMonth));
      const monthName = selectedMonthObj ? selectedMonthObj.name.toLowerCase().replace(/\s*-\s*/g, "_").replace(/\s+/g, "_") : selectedMonth;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Salary_Report_${monthName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Report exported successfully!");
    } catch (error) {
      console.error("Export error", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export report";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const fetchReport = () => {
    if (!selectedMonth) {
      showToast.error("Please select a month first");
      return;
    }

    setLoading(true);
    setError("");

    axios
      .get(`${API_URL}payroll/salary-report/index?smonth=${selectedMonth}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        // Check for hrms_salary_report in response data
        const reportData = response.data.hrms_salary_report || [];
        setSalaryReport(reportData);
        if (response.data.in_report && Array.isArray(response.data.in_report) && response.data.in_report.length > 0) {
          setInReport(response.data.in_report);
        }
        setLoading(false);
        if (reportData && reportData.length > 0) {
          showToast.success("Report loaded successfully!");
        } else {
          showToast.info("No data found for selected month");
        }
      })
      .catch((err) => {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Error fetching salary data";
        setError(errorMessage);
        setLoading(false);
        showToast.error(errorMessage);
        console.error("Fetch report error:", err);
      });
  };

  const fetchMonthList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}payroll/salary-report/month-list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      let list = [];
      if (response.data?.monthlist) {
        list = response.data.monthlist;
      } else if (Array.isArray(response.data)) {
        list = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        list = response.data.data;
      }

      if (!Array.isArray(list) || list.length === 0) {
        console.error("Month list is not an array or is empty:", list);
        setError("No months available or invalid format");
        showToast.error("No months available");
        return;
      }

      const formattedList = list.map((item, index) => ({
        name: item.label || `Month ${item.value}`,
        value: item.value,
      }));

      setMonthList(formattedList);
      if (!toastShownRef.current) {
        showToast.success("Month list loaded successfully!");
        toastShownRef.current = true;
      }
      setError("");
    } catch (err) {
      console.error("Failed to load months", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load month list";
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    setSelectedMonth(value);
    setError("");
    setSalaryReport([]);
    setInReport([]);
  };

  const defaultComponents = [
    { title: "Basic", salary_key: "R_1_R" },
    { title: "HRA", salary_key: "R_2_R" },
    { title: "Medical Allowance", salary_key: "R_3_R" },
    { title: "LTA", salary_key: "R_4_R" },
    { title: "Special Allowance", salary_key: "R_5_R" },
    { title: "PF", salary_key: "R_6_R" },
    { title: "Education Allowance", salary_key: "R_7_R" },
    { title: "Conveyance", salary_key: "R_8_R" },
  ];

  const componentsToRender = inReport && inReport.length > 0 ? inReport : defaultComponents;

  // Box-group classes for Fixed / Earned / Revenue columns (outline on group edges)
  const groupClass = (group, idx, len) =>
    `grp-cell grp-${group}${idx === 0 ? " grp-start" : ""}${idx === len - 1 ? " grp-end" : ""}`;

  const getComponentValue = (dataList, comp) => {
    if (!dataList) return "0.00";
    let list = dataList;
    if (typeof list === "string") {
      try {
        list = JSON.parse(list);
      } catch (e) {
        return "0.00";
      }
    }
    if (Array.isArray(list)) {
      const match = list.find((item) => {
        if (!item) return false;
        if (comp.salary_key && item.salary_key === comp.salary_key) return true;
        if (comp.title && item.title && item.title.trim().toLowerCase() === comp.title.trim().toLowerCase()) return true;
        if (typeof comp === "string") {
          if (item.salary_key && item.salary_key === comp) return true;
          if (item.title && item.title.trim().toLowerCase() === comp.trim().toLowerCase()) return true;
        }
        return false;
      });
      if (match && match.formula_value != null && !isNaN(Number(match.formula_value))) {
        return Number(match.formula_value).toFixed(2);
      }
      return "0.00";
    }
    if (typeof list === "object" && list !== null) {
      const searchKey = typeof comp === "string" ? comp : (comp.title || comp.salary_key || "");
      const normSearchKey = searchKey.toLowerCase().replace(/[\s_-]+/g, "");
      for (const [k, v] of Object.entries(list)) {
        if (k.toLowerCase().replace(/[\s_-]+/g, "") === normSearchKey) {
          return !isNaN(Number(v)) ? Number(v).toFixed(2) : "0.00";
        }
      }
    }
    return "0.00";
  };

  const getOtherTotal = (jsonVal) => {
    if (!jsonVal || jsonVal === "null") return "0.00";
    if (typeof jsonVal === "number") return Number(jsonVal).toFixed(2);
    try {
      const parsed = typeof jsonVal === "string" ? JSON.parse(jsonVal) : jsonVal;
      if (Array.isArray(parsed)) {
        const sum = parsed.reduce((acc, curr) => {
          const amt = curr && (curr.amount != null ? curr.amount : curr.formula_value);
          return acc + (!isNaN(Number(amt)) ? Number(amt) : 0);
        }, 0);
        return sum.toFixed(2);
      } else if (typeof parsed === "object" && parsed !== null) {
        if (parsed.total != null && !isNaN(Number(parsed.total))) {
          return Number(parsed.total).toFixed(2);
        }
        const sum = Object.values(parsed).reduce((acc, curr) => {
          if (typeof curr === "object" && curr !== null) {
            const amt = curr.amount != null ? curr.amount : curr.formula_value;
            return acc + (!isNaN(Number(amt)) ? Number(amt) : 0);
          }
          return acc + (!isNaN(Number(curr)) ? Number(curr) : 0);
        }, 0);
        return sum.toFixed(2);
      } else if (!isNaN(Number(parsed))) {
        return Number(parsed).toFixed(2);
      }
    } catch (e) {
      if (!isNaN(Number(jsonVal))) return Number(jsonVal).toFixed(2);
    }
    return "0.00";
  };

  const renderTableRows = () => {
    if (filteredSalaryReport.length === 0) {
      return (
        <tr>
          <td colSpan={40} className="text-center py-4 text-muted">
            No employee matching &quot;{nameSearch}&quot;
          </td>
        </tr>
      );
    }
    return filteredSalaryReport.map((report, index) => {
      // Parse fixed and earned JSON
      let fixedData = [];
      let earnedData = [];
      try {
        fixedData =
          report.fixed_json && report.fixed_json !== "null"
            ? typeof report.fixed_json === "string"
              ? JSON.parse(report.fixed_json)
              : report.fixed_json
            : [];
        earnedData =
          report.earned_json && report.earned_json !== "null"
            ? typeof report.earned_json === "string"
              ? JSON.parse(report.earned_json)
              : report.earned_json
            : [];
      } catch (e) {
        console.error("Error parsing JSON:", e);
      }

      return (
        <tr key={report["t2_user_code"] || report.user_code || index}>
          <td className="text-center sticky-col-1">{index + 1}</td>
          <td className="text-center sticky-col-2" style={{ fontWeight: 600 }}>{report.user_code}</td>
          <td className="sticky-col-3" style={{ fontWeight: 500, color: "#1e293b" }}>{report.user_fullname}</td>
          <td>{report.user_designation}</td>
          <td className="text-center">{report.employmenttype}</td>
          <td className="text-center">{report.location || "Bangalore"}</td>
          <td className="text-center">{report.total_days}</td>
          <td className="text-center">{report.working_days}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.total_ctc}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.monthly_salary}</td>

          {/* Fixed salary components */}
          {componentsToRender.map((comp, idx) => (
            <td key={`fixed-val-${comp.salary_key || comp.title || idx}`} className={`text-end ${groupClass("fixed", idx, componentsToRender.length)}`} style={{ textAlign: "right" }}>
              {getComponentValue(fixedData, comp)}
            </td>
          ))}

          {/* Earned salary components */}
          {componentsToRender.map((comp, idx) => (
            <td key={`earned-val-${comp.salary_key || comp.title || idx}`} className={`text-end ${groupClass("earned", idx, componentsToRender.length)}`} style={{ textAlign: "right" }}>
              {getComponentValue(earnedData, comp)}
            </td>
          ))}

          {/* Revenue data */}
          <td className={`text-end ${groupClass("revenue", 0, 4)}`} style={{ textAlign: "right" }}>{report.monthly_target}</td>
          <td className={`text-end ${groupClass("revenue", 1, 4)}`} style={{ textAlign: "right" }}>{report.monthly_sales}</td>
          <td className={`text-end ${groupClass("revenue", 2, 4)}`} style={{ textAlign: "right" }}>{report.shortage}</td>
          <td className={`text-end ${groupClass("revenue", 3, 4)}`} style={{ textAlign: "right" }}>{report.earned_incentive}</td>

          {/* Other salary details */}
          <td className="text-end" style={{ textAlign: "right" }}>{report.earned_gross_salary}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.pt}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.tds}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.pf ?? getComponentValue(earnedData, "PF")}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{getOtherTotal(report.other_allowance_json)}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{getOtherTotal(report.other_deduction_json)}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.total_allowance}</td>
          <td className="text-end" style={{ textAlign: "right" }}>{report.total_deduction}</td>
          <td className="text-end" style={{ textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{report.net_salary}</td>
          <td className="text-center">
            <button
              onClick={() => handleDownloadPayslip(report.id)}
              disabled={downloadingIds[report.id] || false}
              className="btn btn-link p-0 border-0 text-primary"
              title="Download Payslip"
              style={{ fontSize: "13px" }}
            >
              {downloadingIds[report.id] ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <FontAwesomeIcon icon={faDownload} />
              )}
            </button>
          </td>
          <td className="text-center">
            <Link to={`/Payroll/SalaryReport/editPayslip/${report.id}`} className="text-primary" title="Edit Payslip" style={{ fontSize: "13px" }}>
              <FontAwesomeIcon icon={faPencil} />
            </Link>
          </td>
        </tr>
      );
    });
  };

  const handleClearAll = () => {
    setSelectedMonth("");
    setSalaryReport([]);
    setNameSearch("");
    setInReport([]);
    setError("");
    showToast.info("All data cleared");
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {loading && <LoadingSpinner />}
        <div className="pb-2 d-flex justify-content-between align-items-center">
          <div className="">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">
                  <i className="ti ti-home"></i> Home
                </Link>
              </li>
              <li className="breadcrumb-item active">Salary Report</li>
            </ul>
          </div>
          <div className="d-flex gap-2">
          <PermissionWrapper module="payroll_salary_report" action="add">
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={handleGenerateReport}
              disabled={!selectedMonth || generating}
            >
              {generating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Generating...
                </>
              ) : (
                <>
                  <i className="ti ti-pencil-alt"></i> Generate Report
                </>
              )}
            </button>
            </PermissionWrapper>

            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={handleExport}
              disabled={!selectedMonth || exporting}
            >
              {exporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="ti ti-export"></i> Export Report
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={handleExportBank}
              disabled={!selectedMonth || bankExporting}
            >
              {bankExporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="ti ti-export"></i> Bank Export Report
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={handleExportAttendance}
              disabled={!selectedMonth || attendanceExporting}
            >
              {attendanceExporting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="ti ti-export"></i> Export Attendance Report
                </>
              )}
            </button>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 pt-3">
            <span className="panel-title">Salary Report</span>
          </div>
          <div className="panel-body">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label className="control-label">Salary Month</label>
                  <select
                    className="form-control"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    <option value="">Select Month</option>
                    {monthList.length > 0 ? (
                      monthList.map((month, idx) => (
                        <option key={idx} value={month.value}>
                          {month.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading months...</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="col-md-8">
                <div style={{ float: "left", marginTop: "25px" }}>
                  <button
                    type="button"
                    onClick={() => fetchReport()}
                    className="btn btn-primary"
                  >
                    Submit
                  </button>
                </div>
                <div
                  style={{
                    float: "left",
                    marginTop: "25px",
                    paddingLeft: "10px",
                  }}
                >
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="btn btn-danger"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {salaryReport.length > 0 ? (
          <div className="panel panel-default with-rounded-corner with-shadow">
            <div className="large-table-container-3">
              <div
                ref={topScrollRef}
                style={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  height: "16px",
                  marginBottom: "2px",
                  background: "#f8f9fa",
                }}
              >
                <div style={{ width: "100%", height: "1px" }}></div>
              </div>
              <div
                ref={tableContainerRef}
                style={{
                  overflowY: "auto",
                  maxHeight: "calc(100vh - 250px)",
                  overflowX: "auto",
                  position: "relative",
                }}
              >
                <table className="table table-condensed table-hover bg-white sticky-table">
                  <thead>
                    <tr>
                      <th className="text-center sticky-col-1" rowSpan="2">
                        SI. No.
                      </th>
                      <th className="text-center sticky-col-2" rowSpan="2">
                        Emp. Code
                      </th>
                      <th className="text-start sticky-col-3" rowSpan="2" style={{ verticalAlign: "top" }}>
                        <div className="d-flex flex-column gap-1">
                          <span>Name</span>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search name..."
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: "11px",
                              height: "24px",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              border: "1px solid #cbd5e1",
                              background: "#ffffff",
                              fontWeight: "normal",
                            }}
                          />
                        </div>
                      </th>
                      <th className="text-start" rowSpan="2">
                        Designation
                      </th>
                      <th className="text-center" rowSpan="2">
                        Employment Type
                      </th>
                      <th className="text-center" rowSpan="2">
                        Location
                      </th>
                      <th className="text-center" rowSpan="2">
                        Total No. of Days
                      </th>
                      <th className="text-center" rowSpan="2">
                        No. of Worked Days
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Total CTC
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Monthly Salary
                      </th>
                      <th className="text-center grp-cell grp-fixed grp-start grp-end grp-title" colSpan={componentsToRender.length}>
                        Fixed
                      </th>
                      <th className="text-center grp-cell grp-earned grp-start grp-end grp-title" colSpan={componentsToRender.length}>
                        Earned
                      </th>
                      <th className="text-center grp-cell grp-revenue grp-start grp-end grp-title" colSpan="4">
                        Revenue
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Earned Gross Salary
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        PT
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        TDS
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        PF
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Other Allowance
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Other Deduction
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Total Allowance
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Total Deduction
                      </th>
                      <th className="text-end" rowSpan="2" style={{ textAlign: "right" }}>
                        Net Salary
                      </th>
                      <th className="text-center" rowSpan="2">
                        Download
                      </th>
                      <th className="text-center" rowSpan="2">
                        Edit
                      </th>
                    </tr>
                    <tr>
                      {componentsToRender.map((comp, idx) => (
                        <th key={`fixed-head-${comp.salary_key || comp.title || idx}`} className={`text-end ${groupClass("fixed", idx, componentsToRender.length)}`} style={{ textAlign: "right" }}>
                          {comp.title}
                        </th>
                      ))}
                      {componentsToRender.map((comp, idx) => (
                        <th key={`earned-head-${comp.salary_key || comp.title || idx}`} className={`text-end ${groupClass("earned", idx, componentsToRender.length)}`} style={{ textAlign: "right" }}>
                          {comp.title}
                        </th>
                      ))}
                      <th className={`text-end ${groupClass("revenue", 0, 4)}`} style={{ textAlign: "right" }}>
                        Monthly Target
                      </th>
                      <th className={`text-end ${groupClass("revenue", 1, 4)}`} style={{ textAlign: "right" }}>
                        Monthly Sales
                      </th>
                      <th className={`text-end ${groupClass("revenue", 2, 4)}`} style={{ textAlign: "right" }}>
                        Shortage
                      </th>
                      <th className={`text-end ${groupClass("revenue", 3, 4)}`} style={{ textAlign: "right" }}>
                        Incentive
                      </th>
                    </tr>
                  </thead>
                  <tbody>{renderTableRows()}</tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel panel-default with-rounded-corner with-shadow p-4 text-center text-muted">
            <p className="mb-0">No Record(s) added yet.</p>
          </div>
        )}
        <style>{`
          .sticky-col-1 {
            position: sticky !important;
            left: 0px !important;
            width: 55px !important;
            min-width: 55px !important;
            max-width: 55px !important;
          }
          .sticky-col-2 {
            position: sticky !important;
            left: 55px !important;
            width: 105px !important;
            min-width: 105px !important;
            max-width: 105px !important;
          }
          .sticky-col-3 {
            position: sticky !important;
            left: 160px !important;
            width: 175px !important;
            min-width: 175px !important;
            box-shadow: 2px 0 5px rgba(0, 0, 0, 0.08) !important;
          }
          thead th.sticky-col-1,
          thead th.sticky-col-2,
          thead th.sticky-col-3 {
            z-index: 25 !important;
            background: #f8f9fa !important;
          }
          tbody td.sticky-col-1,
          tbody td.sticky-col-2,
          tbody td.sticky-col-3 {
            z-index: 5 !important;
            background: #ffffff !important;
          }
          .table-hover tbody tr:hover td.sticky-col-1,
          .table-hover tbody tr:hover td.sticky-col-2,
          .table-hover tbody tr:hover td.sticky-col-3 {
            background-color: #f1f5f9 !important;
          }

          /* Fixed / Earned / Revenue box groups */
          .grp-fixed   { --grp: #3b82f6; --grp-text: #1d4ed8; --grp-head: #dbeafe; --grp-sub: #eff6ff; --grp-body: #f7faff; --grp-hover: #e6f0ff; }
          .grp-earned  { --grp: #16a34a; --grp-text: #15803d; --grp-head: #dcfce7; --grp-sub: #f0fdf4; --grp-body: #f6fdf8; --grp-hover: #e3f8ea; }
          .grp-revenue { --grp: #d97706; --grp-text: #b45309; --grp-head: #fef3c7; --grp-sub: #fffbeb; --grp-body: #fffdf6; --grp-hover: #fdf1d6; }
          .grp-cell {
            --grp-l: 0 0 0 transparent;
            --grp-r: 0 0 0 transparent;
            --grp-t: 0 0 0 transparent;
            --grp-b: 0 0 0 transparent;
            box-shadow: var(--grp-l), var(--grp-r), var(--grp-t), var(--grp-b) !important;
          }
          .grp-cell.grp-start { --grp-l: inset 2px 0 0 var(--grp); }
          .grp-cell.grp-end { --grp-r: inset -2px 0 0 var(--grp); }
          .sticky-table thead th.grp-cell {
            background: var(--grp-sub) !important;
            color: var(--grp-text);
          }
          .sticky-table thead th.grp-title {
            --grp-t: inset 0 3px 0 var(--grp);
            background: var(--grp-head) !important;
            font-weight: 700;
            letter-spacing: 0.06em;
            text-transform: uppercase;
          }
          .sticky-table tbody td.grp-cell {
            background: var(--grp-body) !important;
          }
          .sticky-table.table-hover tbody tr:hover td.grp-cell {
            background: var(--grp-hover) !important;
          }
          .sticky-table tbody tr:last-child td.grp-cell {
            --grp-b: inset 0 -2px 0 var(--grp);
          }
        `}</style>
      </div>
    </div>
  );
};

export default SalaryReport;